import { z } from 'zod';

const localizedStringSchema = z.union([
    z.string(),
    z.record(z.string()),
]).optional();

export const INTEGRATION_MAPPING_STATUSES = ['draft', 'published', 'retired'];
export const INTEGRATION_TRANSFER_KINDS = ['sample', 'preview', 'import', 'sync', 'webhook', 'reconcile'];
export const INTEGRATION_TRANSFER_STATUSES = [
    'queued', 'running', 'succeeded', 'failed', 'cancelled', 'partial',
];
export const INTEGRATION_ITEM_OUTCOMES = [
    'create', 'update', 'unchanged', 'skip', 'conflict', 'invalid', 'failed',
];
export const INTEGRATION_CONFLICT_POLICIES = [
    'remote-authoritative', 'local-authoritative', 'newest-wins', 'manual',
];
export const INTEGRATION_TARGET_ENTITY_TYPES = ['Asset', 'Vulnerability'];

export const INTEGRATION_WRITE_MODES = ['replace', 'append', 'merge-if-empty', 'ignore-if-set'];

export const IntegrationFieldMappingSchema = z.object({
    targetPath: z.string().min(1),
    sourcePath: z.string().optional(),
    transformExpression: z.string().max(4000).optional(),
    defaultValue: z.unknown().optional(),
    required: z.boolean().optional().default(false),
    writeMode: z.enum(INTEGRATION_WRITE_MODES).optional().default('replace'),
    conflictPolicy: z.enum(INTEGRATION_CONFLICT_POLICIES).optional(),
    nullPolicy: z.enum(['ignore', 'clear', 'reject']).optional().default('ignore'),
});

export const IntegrationPartyRoleMappingSchema = z.object({
    roleKey: z.string().min(1),
    sourcePath: z.string().optional(),
    transformExpression: z.string().max(2000).optional(),
    assigneeType: z.enum(['user', 'role', 'unit', 'expression']).optional().default('user'),
});

export const INTEGRATION_REFERENCE_RESOLVE_MODES = ['external-link', 'field-match'];
export const INTEGRATION_REFERENCE_ON_MISSING = ['skip', 'reject-row', 'null'];
export const INTEGRATION_REFERENCE_MERGE_POLICIES = ['append', 'replace'];

export const IntegrationReferenceMappingSchema = z.object({
    targetPath: z.string().min(1),
    cardinality: z.enum(['single', 'multi']).optional().default('multi'),
    resolve: z.object({
        mode: z.enum(INTEGRATION_REFERENCE_RESOLVE_MODES),
        sourcePath: z.string().min(1),
        /** external-link: resourceKey of the mapping that produced the identity link */
        viaResourceKey: z.string().min(1).optional(),
        viaEntityType: z.enum(INTEGRATION_TARGET_ENTITY_TYPES).optional(),
        /** field-match: local entity type + field to query */
        matchEntityType: z.enum(INTEGRATION_TARGET_ENTITY_TYPES).optional(),
        matchField: z.string().min(1).optional(),
    }).superRefine((data, ctx) => {
        if (data.mode === 'external-link') {
            if (!data.viaResourceKey) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'viaResourceKey is required for external-link resolve',
                    path: ['viaResourceKey'],
                });
            }
        }
        if (data.mode === 'field-match') {
            if (!data.matchEntityType) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'matchEntityType is required for field-match resolve',
                    path: ['matchEntityType'],
                });
            }
            if (!data.matchField) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'matchField is required for field-match resolve',
                    path: ['matchField'],
                });
            }
        }
    }),
    onMissing: z.enum(INTEGRATION_REFERENCE_ON_MISSING).optional().default('skip'),
    mergePolicy: z.enum(INTEGRATION_REFERENCE_MERGE_POLICIES).optional().default('append'),
    /** Dual-write: $addToSet this entity id onto each resolved ref at inverse.targetPath */
    inverse: z.object({
        targetPath: z.string().min(1),
    }).optional(),
});

export const IntegrationRowSourceSubFetchSchema = z.object({
    resourceKey: z.string().min(1).max(100),
    pathParamsFromRow: z.record(z.string()).optional().default({}),
    queryParamsFromRow: z.record(z.string()).optional().default({}),
    viewKey: z.string().max(100).optional(),
});

/**
 * Expand parent pull rows into child rows (nested array and/or per-row drill-down).
 * Child rows get `_parent` with selected parent fields + resourceContext path params.
 */
export const IntegrationRowSourceSchema = z.object({
    itemsPath: z.string().min(1).max(200).optional(),
    subFetch: IntegrationRowSourceSubFetchSchema.optional(),
    parentFields: z.array(z.string().min(1)).optional(),
}).refine(
    (data) => Boolean(data.itemsPath || data.subFetch),
    { message: 'rowSource requires itemsPath and/or subFetch' },
);

export const INTEGRATION_MATCH_OPERATORS = ['and', 'or'];

export const IntegrationMatchRuleFieldSchema = z.object({
    sourcePath: z.string().min(1),
    targetField: z.string().min(1),
    transformExpression: z.string().max(2000).optional(),
    caseSensitive: z.boolean().optional().default(true),
});

export const IntegrationMatchRuleSchema = z.object({
    operator: z.enum(INTEGRATION_MATCH_OPERATORS).optional().default('and'),
    fields: z.array(IntegrationMatchRuleFieldSchema).min(1),
});

export const INTEGRATION_PIPELINE_ACTIONS = ['create-or-match', 'create-always', 'match-only'];

export const IntegrationPipelineLinkSchema = z.object({
    stepKey: z.string().min(1),
    targetPath: z.string().min(1),
    mode: z.enum(['append', 'replace']).optional().default('append'),
});

export const IntegrationPipelineStepSchema = z.object({
    stepKey: z.string().min(1).max(50),
    entityType: z.enum(INTEGRATION_TARGET_ENTITY_TYPES),
    action: z.enum(INTEGRATION_PIPELINE_ACTIONS).optional().default('create-or-match'),
    matchRules: z.array(IntegrationMatchRuleSchema).optional().default([]),
    fieldMappings: z.array(IntegrationFieldMappingSchema).optional().default([]),
    referenceMappings: z.array(IntegrationReferenceMappingSchema).optional().default([]),
    linkTo: IntegrationPipelineLinkSchema.optional(),
});

export const IntegrationResourceContextSchema = z.object({
    pathParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    queryParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    multiViewKey: z.string().max(100).optional(),
}).optional().default({});

export const CreateIntegrationMappingSchema = z.object({
    connectionId: z.string().min(1),
    key: z.string().trim().min(1).max(100).regex(/^[a-z0-9][a-z0-9_-]*$/i),
    name: z.union([z.string().min(1), z.record(z.string())]),
    description: localizedStringSchema,
    resourceKey: z.string().trim().min(1).max(100),
    targetEntityType: z.enum(INTEGRATION_TARGET_ENTITY_TYPES),
    externalIdSourcePath: z.string().min(1).optional(),
    externalIdTransformExpression: z.string().max(2000).optional(),
    resourceContext: IntegrationResourceContextSchema,
    rowSource: IntegrationRowSourceSchema.optional().nullable(),
    fieldMappings: z.array(IntegrationFieldMappingSchema).default([]),
    referenceMappings: z.array(IntegrationReferenceMappingSchema).optional().default([]),
    partyRoleMappings: z.array(IntegrationPartyRoleMappingSchema).optional().default([]),
    matchRules: z.array(IntegrationMatchRuleSchema).optional().default([]),
    pipelineSteps: z.array(IntegrationPipelineStepSchema).optional().default([]),
    conflictPolicy: z.enum(INTEGRATION_CONFLICT_POLICIES).optional().default('manual'),
    deletePolicy: z.enum(['ignore', 'soft-delete', 'flag']).optional().default('ignore'),
    /** @deprecated Use referenceMappings. Kept for backward-compatible reads. */
    referencePolicy: z.record(z.unknown()).optional().default({}),
    customFields: z.record(z.unknown()).optional(),
});

export const UpdateIntegrationMappingSchema = CreateIntegrationMappingSchema.partial().omit({
    connectionId: true,
    key: true,
});

export const PublishIntegrationMappingSchema = z.object({
    note: z.string().max(1000).optional(),
});

export const PreviewImportSchema = z.object({
    sampleLimit: z.coerce.number().int().min(1).max(200).optional().default(50),
    pathParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    queryParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    multiViewKey: z.string().max(100).optional(),
});

export const CommitImportSchema = z.object({
    previewToken: z.string().min(16),
    batchSize: z.coerce.number().int().min(1).max(100).optional().default(25),
    dryRun: z.boolean().optional().default(false),
});

export const UpsertIntegrationSyncStateSchema = z.object({
    mappingKey: z.string().min(1),
    mappingId: z.string().optional(),
    periodType: z.enum(['once', 'periodic']).optional().default('periodic'),
    intervalValue: z.coerce.number().int().min(1).max(999).optional(),
    intervalUnit: z.enum(['minute', 'hour', 'day', 'week']).optional(),
    firstRunAt: z.union([z.string(), z.coerce.date()]).optional().nullable(),
    /** Optional legacy; server regenerates from period fields when present. */
    cron: z.string().min(1).max(200).optional(),
    timezone: z.string().min(1).max(100).optional(),
    isPaused: z.boolean().optional().default(false),
    cursor: z.record(z.unknown()).optional(),
}).superRefine((data, ctx) => {
    if (data.periodType === 'periodic') {
        if (data.intervalValue == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'intervalValue is required for periodic schedules',
                path: ['intervalValue'],
            });
        }
        if (!data.intervalUnit) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'intervalUnit is required for periodic schedules',
                path: ['intervalUnit'],
            });
        }
    }
    if (data.periodType === 'once' && !data.firstRunAt && !data.cron) {
        // firstRunAt optional → run ASAP; OK
    }
});

export const ResolveConflictSchema = z.object({
    resolution: z.enum(['keep-local', 'take-remote', 'merge']),
    mergedValues: z.record(z.unknown()).optional(),
    note: z.string().max(2000).optional(),
});

export const ExecuteProviderActionSchema = z.object({
    actionKey: z.string().min(1),
    input: z.record(z.unknown()).optional().default({}),
    idempotencyKey: z.string().min(8).max(200).optional(),
    confirm: z.boolean().optional().default(false),
});

export const UpgradeProviderSchema = z.object({
    targetVersion: z.number().int().positive(),
    dryRun: z.boolean().optional().default(true),
});

export const DiscoverResourceSchema = z.object({
    sampleLimit: z.coerce.number().int().min(1).max(100).optional().default(25),
    pathParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    queryParams: z.record(z.union([z.string(), z.number(), z.boolean()])).optional().default({}),
    multiViewKey: z.string().max(100).optional(),
});
